import Joi from "joi-browser";
import { useMemo, useState } from "react";
import { putTTSSettings } from "../../service";
import Form from "V2.0/components/Form";
import { message } from "antd";

enum CARTESIA_MODEL {
  SONIC = "sonic",
  SONIC_2 = "sonic-2",
  SONIC_3 = "sonic-3",
}

enum VOICE_SPEED {
  SLOW = "slow",
  FAST = "fast",
  NORMAL = "normal",
}

enum LANGUAGE {
  EN = "en",
  FR = "fr",
  DE = "de",
  ES = "es",
  PT = "pt",
  ZH = "zh",
  JA = "ja",
  HI = "hi",
  IT = "it",
  KO = "ko",
  NL = "nl",
  PL = "pl",
  RU = "ru",
  SV = "sv",
  TR = "tr",
}
enum SONIC_3_LANGUAGE {
  TE = "te",
  GU = "gu",
  KN = "kn",
  ML = "ml",
  MR = "mr",
  PA = "pa",
  TA = "ta",
}

const LanguageNames = {
  [LANGUAGE.EN]: "English",
  [LANGUAGE.FR]: "French",
  [LANGUAGE.DE]: "German",
  [LANGUAGE.HI]: "Hindi",
  [SONIC_3_LANGUAGE.GU]: "Gujarati",
  [SONIC_3_LANGUAGE.KN]: "Kannada",
  [SONIC_3_LANGUAGE.ML]: "Malayalam",
  [SONIC_3_LANGUAGE.MR]: "Marathi",
  [SONIC_3_LANGUAGE.PA]: "Punjabi",
  [SONIC_3_LANGUAGE.TA]: "Tamil",
  [SONIC_3_LANGUAGE.TE]: "Telugu",
  [LANGUAGE.ES]: "Spanish",
  [LANGUAGE.PT]: "Portuguese",
  [LANGUAGE.ZH]: "Chinese",
  [LANGUAGE.JA]: "Japanese",
  [LANGUAGE.IT]: "Italian",
  [LANGUAGE.KO]: "Korean",
  [LANGUAGE.NL]: "Dutch",
  [LANGUAGE.PL]: "Polish",
  [LANGUAGE.RU]: "Russian",
  [LANGUAGE.SV]: "Swedish",
  [LANGUAGE.TR]: "Turkish",
};

interface CartesiaAISettings {
  bufferSize: number;
  cartesiaLanguage: LANGUAGE;
  voiceSpeed: VOICE_SPEED;
  voiceSpeedSonic3: number;
  cartesiaModel: CARTESIA_MODEL;
}

const defaultLanguages = Object.values(LANGUAGE);
const sonic3Languages = [...defaultLanguages, ...Object.values(SONIC_3_LANGUAGE)];

function getCartesiaLanguageTemplate(languages) {
  const options = languages.map((language, idx) => {
    return { id: idx, value: language, label: LanguageNames[language] };
  });

  const template = {
    name: "cartesiaLanguage",
    label: "Language",
    type: "select",
    options,
  };

  return template;
}

const voiceSpeedTemplate = {
  name: "voiceSpeed",
  label: "Voice Speed",
  type: "select",
  options: Object.values(VOICE_SPEED).map((voiceSpeed, idx) => {
    return { id: idx, value: voiceSpeed, label: voiceSpeed };
  }),
};

const voiceSpeedSonic3Template = {
  name: "voiceSpeedSonic3",
  label: "Voice Speed",
  type: "slider",
  min: 0.6,
  step: 0.1,
  max: 1.5,
};

const bufferSizeTemplate = {
  name: "bufferSize",
  label: "Buffer Size",
  type: "slider",
  min: 100,
  max: 1000,
  step: 100,
};

const defaultCartesiaTemplate = [
  getCartesiaLanguageTemplate(defaultLanguages),
  voiceSpeedTemplate,
  bufferSizeTemplate,
];

const sonic3Template = [
  getCartesiaLanguageTemplate(sonic3Languages),
  voiceSpeedSonic3Template,
  bufferSizeTemplate,
];

const cartesiaSchema = {
  cartesiaLanguage: Joi.string().required().label("Language"),
  // cartesiaModel: Joi.string().required().label("Model"),
  voiceSpeed: Joi.string().required().label("Voice Speed"),
  voiceSpeedSonic3: Joi.number().required().label("Voice Speed"),
  bufferSize: Joi.number().required().label("Buffer Size"),
};

const defaultCartesiaSchema = (() => {
  const { cartesiaLanguage, voiceSpeed, bufferSize } = cartesiaSchema;

  return {
    cartesiaLanguage,
    voiceSpeed,
    bufferSize,
  };
})();

const sonic3Schema = (() => {
  const { cartesiaLanguage, voiceSpeedSonic3, bufferSize } = cartesiaSchema;

  return {
    cartesiaLanguage,
    voiceSpeedSonic3,
    bufferSize,
  };
})();

function getCartesiaAIConfigurableSettings(settings) {
  const {
    cartesiaLanguage,
    cartesiaModel,
    voiceSpeedSonic3,
    bufferSize,
    voiceSpeed,
  } = settings;

  if (cartesiaModel === CARTESIA_MODEL.SONIC_3) {
    const inputField = { cartesiaLanguage, voiceSpeedSonic3, bufferSize };

    return {
      inputField,
      template: sonic3Template,
      schema: sonic3Schema,
    };
  }

  return {
    inputField: { cartesiaLanguage, voiceSpeed, bufferSize },
    template: defaultCartesiaTemplate,
    schema: defaultCartesiaSchema,
  };
}

export default function Cartesia(props) {
  const {
    cartesiaAISettings,
    ttsServiceProvider,
    syncAssistant,
    workspaceId,
    assistantId,
    onClose,
  } = props;

  const [loading, setLoading] = useState(false);
  const { inputField, template, schema } = useMemo(() => {
    return getCartesiaAIConfigurableSettings(cartesiaAISettings);
  }, [cartesiaAISettings]);

  const handleSaveSettings = async (data) => {
    setLoading(true);
    message.loading("Saving");

    const payload = {
      ttsServiceProvider,
      cartesiaAISettings: data,
    };

    try {
      const response = await putTTSSettings({
        workspaceId,
        assistantId,
        payload,
      });

      if (response.status === 200) {
        message.destroy();
        setLoading(false);
        message.success("Saved");
        syncAssistant((prevAssistant) => {
          return { ...prevAssistant, ttsSettings: response.data };
        });
        onClose();
      }
    } catch (e) {
      setLoading(false);

      message.destroy();
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          e.response.data?.error ||
          "Failed to save settings",
      );
    }
  };

  return (
    <div>
      <Form
        inputField={inputField}
        template={template}
        schema={schema}
        buttonInfo={{ label: "Save", style: "right-side" }}
        loading={loading}
        setLoading={setLoading}
        onSubmit={handleSaveSettings}
      />
    </div>
  );
}
